import { createRoot } from "react-dom/client";
import { PostHogProvider } from "@posthog/react";
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import App from "./App.tsx";
import "./index.css";
import i18n from "./i18n";
import { initSentry } from "./lib/sentry";
import {
  markAppInitStart,
  markAppInitEnd,
  initWebVitalsTracking,
} from "./lib/monitoring";
import { validateEnv, isDevelopment } from "./lib/env";
import { getPostHogApiKey, getPostHogOptions } from "./lib/posthog";

let lastStableViewportHeight = 0;
let lastStableViewportWidth = 0;
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function isEditableElement(element: Element | null): boolean {
  if (!element) return false;

  if (element instanceof HTMLTextAreaElement) return true;

  if (element instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(element.type);
  }

  return element instanceof HTMLElement && element.isContentEditable;
}

function syncAppViewportHeight() {
  if (typeof window === "undefined") {
    return;
  }

  const visualViewport = window.visualViewport;
  const visualViewportHeight = visualViewport?.height;
  const visualViewportWidth = visualViewport?.width;
  const viewportHeight =
    typeof visualViewportHeight === "number" && visualViewportHeight > 0
      ? visualViewportHeight
      : window.innerHeight;
  const viewportWidth =
    typeof visualViewportWidth === "number" && visualViewportWidth > 0
      ? visualViewportWidth
      : window.innerWidth;
  const heightDrop =
    lastStableViewportHeight > 0
      ? lastStableViewportHeight - viewportHeight
      : 0;
  const widthDelta =
    lastStableViewportWidth > 0
      ? Math.abs(lastStableViewportWidth - viewportWidth)
      : 0;
  const activeElement = document.activeElement;
  const editableFocused = isEditableElement(activeElement);
  const isKeyboardResize =
    heightDrop > 120 && widthDelta < 2 && (editableFocused || heightDrop > 220);

  if (!isKeyboardResize) {
    lastStableViewportHeight = viewportHeight;
    lastStableViewportWidth = viewportWidth;
  }

  const nextHeight =
    isKeyboardResize && lastStableViewportHeight > 0
      ? lastStableViewportHeight
      : viewportHeight;

  document.documentElement.style.setProperty(
    "--app-vh",
    `${Math.ceil(nextHeight)}px`,
  );
}

function removeBootShell() {
  const bootShell = document.getElementById("app-boot-shell");
  if (bootShell) {
    bootShell.remove();
  }
}

// Start performance tracking
markAppInitStart();

// Validate environment variables
const envResult = validateEnv();
if (!envResult.valid) {
  const errorMessage = `Environment configuration error:\n${envResult.errors.join("\n")}`;
  console.error(errorMessage);

  // In development, show error UI instead of crashing
  if (isDevelopment()) {
    const root = document.getElementById("root")!;
    removeBootShell();
    root.innerHTML = `
      <div style="padding: 2rem; font-family: system-ui; max-width: 600px; margin: 2rem auto;">
        <h1 style="color: #e11d48; margin-bottom: 1rem;">⚠️ ${i18n.t("main.envConfigErrorTitle")}</h1>
        <p style="color: #666; margin-bottom: 1rem;">${i18n.t("main.envConfigErrorDesc")}</p>
        <ul style="background: #fef2f2; padding: 1rem 2rem; border-radius: 8px; color: #991b1b;">
          ${envResult.errors.map((e) => `<li style="margin: 0.5rem 0;">${e}</li>`).join("")}
        </ul>
        <p style="color: #666; margin-top: 1rem;">
          ${i18n.t("main.checkEnvFilePrefix")} <code style="background: #f1f5f9; padding: 2px 6px; border-radius: 4px;">.env</code> ${i18n.t("main.checkEnvFileSuffix")}
        </p>
      </div>
    `;
  }
  throw new Error(errorMessage);
}

// Initialize Sentry before rendering
initSentry();

// Initialize Web Vitals tracking
initWebVitalsTracking();

// Keep a stable viewport height CSS variable to avoid iOS Safari toolbar gaps.
syncAppViewportHeight();
window.addEventListener("resize", syncAppViewportHeight);
window.addEventListener("orientationchange", syncAppViewportHeight);
window.addEventListener("pageshow", syncAppViewportHeight);
window.addEventListener("focus", syncAppViewportHeight);
window.visualViewport?.addEventListener("resize", syncAppViewportHeight);

const posthogApiKey = getPostHogApiKey();
const posthogOptions = getPostHogOptions();

// Render the app
createRoot(document.getElementById("root")!).render(
  posthogApiKey ? (
    <PostHogProvider apiKey={posthogApiKey} options={posthogOptions}>
      <App />
    </PostHogProvider>
  ) : (
    <App />
  ),
);

// Keep first paint cheap, then remove the static boot shell after React mounts.
requestAnimationFrame(() => {
  requestAnimationFrame(removeBootShell);
});
window.setTimeout(removeBootShell, 4000);

// Mark app initialization complete
markAppInitEnd();
